import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Row, Col, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { FormPageLayout } from '../../components/FormPageLayout';
import { useAutoSave } from '../../hooks/useAutoSave';

const NEGATIVE_TYPE_OPTIONS = {
    keyword_exact: '否定关键词-精准',
    keyword_phrase: '否定关键词-词组',
    asin: '否定ASIN'
};

const NEGATIVE_REASON_OPTIONS = {
    irrelevant: '不相关',
    low_conversion: '转化率低',
    high_cpc: 'CPC过高',
    wrong_audience: '受众不匹配'
};

const NEGATIVE_LEVEL_OPTIONS = {
    campaign: 'Campaign级别',
    ad_group: 'Ad Group级别'
};

const NegativeKeywordEdit: React.FC = () => {
    const { skuId, id } = useParams<{ skuId: string; id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const isNew = !id || id === 'new';

    const { triggerSave, state: saveState } = useAutoSave<any>({
        onSave: async (data) => {
            if (isNew) {
                await api.post(`/operation-strategy/${skuId}/negative-keywords/`, data);
                message.success('否定词已创建');
                navigate(`/operation-strategy/${skuId}`);
            } else {
                await api.put(`/operation-strategy/${skuId}/negative-keywords/${id}/`, data);
            }
        },
        delay: 1000,
        enabled: !isNew,
        showMessage: true
    });

    useEffect(() => {
        if (!isNew) {
            fetchNegativeKeyword();
        } else {
            form.setFieldsValue({
                negative_type: 'keyword_exact',
                reason: 'irrelevant',
                level: 'campaign'
            });
        }
    }, [id]);

    const fetchNegativeKeyword = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/operation-strategy/${skuId}/negative-keywords/${id}/`);
            form.setFieldsValue(res.data);
        } catch (error) {
            message.error('加载失败');
            navigate(`/operation-strategy/${skuId}`);
        } finally {
            setLoading(false);
        }
    };

    const handleValuesChange = (_: any, allValues: any) => {
        if (!isNew) {
            triggerSave(allValues);
        }
    };

    const handleManualSave = async () => {
        try {
            const values = await form.validateFields();
            await api.post(`/operation-strategy/${skuId}/negative-keywords/`, values);
            message.success('否定词已创建');
            navigate(`/operation-strategy/${skuId}`);
        } catch (error) {
            // Validation error
        }
    };

    const getSaveStatus = () => {
        if (saveState.isSaving) return 'saving';
        if (saveState.hasUnsavedChanges) return 'hasChanges';
        if (saveState.lastSavedAt) return 'saved';
        return 'idle';
    };

    return (
        <FormPageLayout
            title={isNew ? '添加否定词' : '编辑否定词'}
            breadcrumbs={[
                { title: '运营策略', path: '/operation-strategy' },
                { title: 'SKU', path: `/operation-strategy/${skuId}` },
                { title: isNew ? '添加否定词' : '编辑否定词' }
            ]}
            saveStatus={getSaveStatus()}
            showSaveButton={isNew}
            onSave={handleManualSave}
            saving={loading}
        >
            <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="content"
                            label="否定内容"
                            rules={[{ required: true, message: '请输入否定内容' }]}
                            extra="输入要否定的关键词或ASIN，多个请换行分隔"
                        >
                            <Input.TextArea placeholder="输入关键词或ASIN" rows={3} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="negative_type" label="否定类型" rules={[{ required: true }]}>
                            <Select options={Object.entries(NEGATIVE_TYPE_OPTIONS).map(([value, label]) => ({ value, label }))} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="reason" label="否定原因">
                            <Select options={Object.entries(NEGATIVE_REASON_OPTIONS).map(([value, label]) => ({ value, label }))} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="level" label="否定级别">
                            <Select options={Object.entries(NEGATIVE_LEVEL_OPTIONS).map(([value, label]) => ({ value, label }))} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="campaign_name" label="Campaign名称">
                            <Input placeholder="可选" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="notes" label="备注">
                            <Input.TextArea rows={2} placeholder="可选备注" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </FormPageLayout>
    );
};

export default NegativeKeywordEdit;
